import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Package, ChevronRight, Filter,
  Image as ImageIcon, Video, MoreVertical, Edit, Upload,
  FileSpreadsheet, Loader2, ArrowLeft, Globe, MapPin, ShieldCheck,
  Waves, CircuitBoard, BatteryFull, Zap, Wind, Gamepad2,
  Puzzle, Cog, ShoppingBag, Shirt, Tag, Wrench, LayoutGrid, MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const categoryLabels = {
  efoil: 'E-Foil',
  esurf: 'E-Surf',
  boat: 'Bateau électrique',
  accessory: 'Accessoire'
};

const categoryColors = {
  efoil: 'bg-[#152A35] text-[#3498DB] border-[#3498DB]/20',
  esurf: 'bg-[#142915] text-[#00E01A] border-[#00E01A]/20',
  boat: 'bg-[#2A2010] text-[#F39C12] border-[#F39C12]/20',
  accessory: 'bg-[#2C3338] text-[#8C8F94] border-[#8C8F94]/20'
};

// Granular sub-category mapping from specs.product_type
const subCategoryMap = {
  'Board Only Carbon': 'boards', 'Board Only Fibreglass': 'boards', 'Board Only AIR': 'boards',
  'Board Only Soft Top 2': 'boards', 'Board Only Hard Top': 'boards', 'Board Only Flitescooter': 'boards', 'Board Only': 'boards',
  'eFoil System Conical': 'systems', 'eFoil System Conical Carbon': 'systems', 'eFoil System Standard': 'systems',
  'Flitecell': 'batteries', 'Flitecell Part': 'batteries',
  'Flitecell Charger': 'chargers', 'Flitecell Charger Part': 'chargers',
  'Front Wing Conical': 'wings', 'Front Wing Standard': 'wings', 'Stabiliser': 'wings', 'Wings Part': 'wings',
  'Flite Controller Part': 'controllers', 'Controller & Accessories Pouch': 'controllers',
  'Board Part': 'board_parts', 'Board Component': 'board_parts', 'Board Component incl Bag': 'board_parts',
  'eFoil Part': 'efoil_parts', 'eFoil Component': 'efoil_parts', 'eFoil System Component': 'efoil_parts',
  'Bag': 'bags',
  'Apparel': 'apparel', 'Vest': 'apparel', 'Helmet': 'apparel',
  'Merchandising & Display': 'merch', 'Merchandise': 'merch', 'Printed Material': 'merch',
  'Flite Service Tool': 'tools', 'Public Tool': 'tools', 'Public tool': 'tools',
};

const subCategoryLabels = {
  boards: 'Planches',
  systems: 'Systèmes eFoil complets',
  batteries: 'Batteries (Flitecell)',
  chargers: 'Chargeurs',
  wings: 'Ailes & Stabilisateurs',
  controllers: 'Contrôleurs',
  board_parts: 'Pièces Board',
  efoil_parts: 'Pièces eFoil',
  bags: 'Sacs & Housses',
  apparel: 'Vêtements & Protection',
  merch: 'Merchandising',
  tools: 'Outillage',
  other: 'Autres',
};

const subCategoryColors = {
  boards: 'bg-[#152A35] text-[#3498DB] border-[#3498DB]/30',
  systems: 'bg-[#0F2A1A] text-[#00E01A] border-[#00E01A]/30',
  batteries: 'bg-[#2A2510] text-[#F1C40F] border-[#F1C40F]/30',
  chargers: 'bg-[#2A2010] text-[#F39C12] border-[#F39C12]/30',
  wings: 'bg-[#1A1535] text-[#9B59B6] border-[#9B59B6]/30',
  controllers: 'bg-[#152535] text-[#1ABC9C] border-[#1ABC9C]/30',
  board_parts: 'bg-[#1D2327] text-[#8C8F94] border-[#52555A]/30',
  efoil_parts: 'bg-[#1D2327] text-[#8C8F94] border-[#52555A]/30',
  bags: 'bg-[#251520] text-[#E74C8B] border-[#E74C8B]/30',
  apparel: 'bg-[#251515] text-[#E74C3C] border-[#E74C3C]/30',
  merch: 'bg-[#2C3338] text-[#8C8F94] border-[#8C8F94]/20',
  tools: 'bg-[#2C3338] text-[#8C8F94] border-[#8C8F94]/20',
  other: 'bg-[#2C3338] text-[#8C8F94] border-[#8C8F94]/20',
};

// Ordered display priority
const subCategoryOrder = ['boards', 'systems', 'batteries', 'chargers', 'wings', 'controllers', 'board_parts', 'efoil_parts', 'bags', 'apparel', 'merch', 'tools', 'other'];

const subCategoryIcons = {
  boards: Waves,
  systems: CircuitBoard,
  batteries: BatteryFull,
  chargers: Zap,
  wings: Wind,
  controllers: Gamepad2,
  board_parts: Puzzle,
  efoil_parts: Cog,
  bags: ShoppingBag,
  apparel: Shirt,
  merch: Tag,
  tools: Wrench,
  other: MoreHorizontal,
};

const getSubCategory = (product) => {
  const pt = product.specs?.product_type;
  if (pt && subCategoryMap[pt]) return subCategoryMap[pt];
  return 'other';
};

const relationLabels = {
  exclusive_importer: 'Importateur exclusif',
  authorized_reseller: 'Revendeur agréé',
  distributor: 'Distributeur',
  direct: 'Direct'
};

const brandImages = {
  'Fliteboard': 'https://images.pexels.com/photos/36164293/pexels-photo-36164293.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'Awake': 'https://images.pexels.com/photos/4592026/pexels-photo-4592026.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
};

const ProductForm = ({ product, suppliers, onSave, onClose }) => {
  const [formData, setFormData] = useState(product || {
    supplier_id: '',
    reference: '',
    name: '',
    category: 'esurf',
    public_price_ht: '',
    purchase_price_ht: '',
    sale_price_ttc: '',
    specs: {},
    photos: [],
    videos: [],
    status: 'active'
  });

  const handlePublicHtChange = (val) => {
    const ht = parseFloat(val);
    if (!isNaN(ht) && ht > 0) {
      setFormData({
        ...formData,
        public_price_ht: val,
        sale_price_ttc: (ht * 1.2).toFixed(2),
        purchase_price_ht: (ht * 0.85).toFixed(2)
      });
    } else {
      setFormData({ ...formData, public_price_ht: val });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      public_price_ht: formData.public_price_ht ? parseFloat(formData.public_price_ht) : null,
      purchase_price_ht: formData.purchase_price_ht ? parseFloat(formData.purchase_price_ht) : null,
      sale_price_ttc: formData.sale_price_ttc ? parseFloat(formData.sale_price_ttc) : null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="supplier">Fournisseur *</Label>
          <Select value={formData.supplier_id} onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}>
            <SelectTrigger data-testid="product-supplier-select"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
            <SelectContent>
              {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="category">Catégorie *</Label>
          <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="efoil">E-Foil</SelectItem>
              <SelectItem value="esurf">E-Surf</SelectItem>
              <SelectItem value="boat">Bateau électrique</SelectItem>
              <SelectItem value="accessory">Accessoire</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="reference">Référence *</Label>
          <Input id="reference" value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} placeholder="Ex: RAVIK-S-22" required data-testid="product-reference-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="name">Nom du produit *</Label>
          <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Awake RÄVIK S 22" required data-testid="product-name-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="public_price_ht">Prix public HT</Label>
          <Input id="public_price_ht" type="number" value={formData.public_price_ht} onChange={(e) => handlePublicHtChange(e.target.value)} placeholder="9990" data-testid="product-public-price-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="purchase_price">Prix achat HT (auto -15%)</Label>
          <Input id="purchase_price" type="number" value={formData.purchase_price_ht} onChange={(e) => setFormData({ ...formData, purchase_price_ht: e.target.value })} placeholder="8491.50" data-testid="product-purchase-price-input" />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="sale_price">Prix vente TTC (auto +20%)</Label>
          <Input id="sale_price" type="number" value={formData.sale_price_ttc} onChange={(e) => setFormData({ ...formData, sale_price_ttc: e.target.value })} placeholder="11988" data-testid="product-sale-price-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="status">Statut</Label>
          <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
            <SelectTrigger data-testid="product-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Actif</SelectItem>
              <SelectItem value="discontinued">Discontinué</SelectItem>
              <SelectItem value="on_order">Sur commande</SelectItem>
              <SelectItem value="in_development">En développement</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose} data-testid="product-cancel-btn">Annuler</Button>
        <Button type="submit" className="bg-[#00E01A] hover:bg-[#00B815] text-black" data-testid="product-save-btn">{product ? 'Mettre à jour' : 'Créer'}</Button>
      </div>
    </form>
  );
};

const ExcelImportDialog = ({ suppliers, onImport, onClose }) => {
  const [file, setFile] = useState(null);
  const [supplierId, setSupplierId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleImport = async () => {
    if (!file || !supplierId) { toast.error('Sélectionnez un fichier et un fournisseur'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await onImport(formData, supplierId);
      toast.success(`Import terminé: ${result.imported} créé(s), ${result.updated} mis à jour`);
      if (result.errors?.length > 0) toast.warning(`${result.errors.length} erreur(s) lors de l'import`);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Fournisseur *</Label>
        <Select value={supplierId} onValueChange={setSupplierId}>
          <SelectTrigger data-testid="import-supplier-select"><SelectValue placeholder="Sélectionner le fournisseur..." /></SelectTrigger>
          <SelectContent>{suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${file ? 'border-[#00E01A] bg-[#00E01A]/5' : 'border-[#2C3338] hover:border-[#00E01A]/50'}`}>
        <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" id="excel-upload" data-testid="excel-upload-input" />
        <label htmlFor="excel-upload" className="cursor-pointer">
          {file ? (
            <div className="flex items-center justify-center gap-2 text-[#00E01A]"><FileSpreadsheet size={24} /><span>{file.name}</span></div>
          ) : (
            <><FileSpreadsheet size={32} className="mx-auto text-[#8C8F94] mb-2" /><p className="text-[#8C8F94]">Cliquez pour sélectionner un fichier Excel</p><p className="text-[#52555A] text-xs mt-1">.xlsx, .xls ou .csv</p></>
          )}
        </label>
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button onClick={handleImport} disabled={!file || !supplierId || loading} className="bg-[#00E01A] hover:bg-[#00B815] text-black" data-testid="import-excel-btn">
          {loading ? (<><Loader2 size={16} className="animate-spin mr-2" />Import en cours...</>) : (<><Upload size={16} className="mr-2" />Importer</>)}
        </Button>
      </div>
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductCard = ({ product, canSeePrices, onEdit, onPhotoUpload }) => (
  <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] overflow-hidden hover:border-[#00E01A]/30 transition-all duration-200 group">
    <div className="aspect-video bg-[#141617] relative">
      {product.photos && product.photos.length > 0 ? (
        <img src={product.photos[0].startsWith('/api') ? `${API_URL}${product.photos[0]}` : product.photos[0]} alt={product.name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center"><Package size={48} className="text-[#2C3338]" /></div>
      )}
      <div className="absolute top-2 right-2 flex gap-1">
        {product.photos?.length > 0 && (<span className="flex items-center gap-1 bg-black/60 backdrop-blur px-2 py-1 rounded text-xs text-white"><ImageIcon size={12} />{product.photos.length}</span>)}
        {product.videos?.length > 0 && (<span className="flex items-center gap-1 bg-black/60 backdrop-blur px-2 py-1 rounded text-xs text-white"><Video size={12} />{product.videos.length}</span>)}
      </div>
      {/* Inline photo upload */}
      {onPhotoUpload && (
        <label className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 backdrop-blur hover:bg-[#00E01A] hover:text-black px-2 py-1.5 rounded-lg text-xs text-white cursor-pointer transition-all" data-testid={`upload-photo-${product.id}`}>
          <Upload size={12} />
          Photo
          <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && onPhotoUpload(product.id, e.target.files[0])} />
        </label>
      )}
    </div>
    <div className="p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-[#8C8F94] text-xs font-mono">{product.reference}</p>
          <h3 className="text-white font-semibold mt-1">{product.name}</h3>
          <p className="text-[#8C8F94] text-sm">{product.supplier_name}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 text-[#8C8F94] hover:text-white transition-colors" data-testid={`product-menu-${product.id}`}><MoreVertical size={18} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#1D2327] border-[#2C3338]">
            <DropdownMenuItem onClick={() => onEdit(product)} className="text-white hover:bg-[#2C3338]"><Edit size={14} className="mr-2" /> Modifier</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${subCategoryColors[getSubCategory(product)] || subCategoryColors.other}`}>
          {subCategoryLabels[getSubCategory(product)] || product.specs?.product_type || categoryLabels[product.category]}
        </span>
        {product.status !== 'active' && (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#2C3338] text-[#8C8F94]">
            {product.status === 'discontinued' ? 'Discontinué' : product.status === 'on_order' ? 'Sur commande' : 'En dev'}
          </span>
        )}
      </div>
      {canSeePrices && product.public_price_ht ? (
        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#2C3338]" data-testid={`product-prices-${product.id}`}>
          <div>
            <p className="text-[#8C8F94] text-[10px] leading-tight">Public TTC</p>
            <p className="text-[#00E01A] font-bold text-sm">{product.sale_price_ttc ? `${product.sale_price_ttc.toLocaleString('fr-FR')} €` : '-'}</p>
          </div>
          <div className="text-center">
            <p className="text-[#8C8F94] text-[10px] leading-tight">Public HT</p>
            <p className="text-white font-semibold text-sm">{product.public_price_ht.toLocaleString('fr-FR')} €</p>
          </div>
          <div className="text-right">
            <p className="text-[#8C8F94] text-[10px] leading-tight">Achat HT</p>
            <p className="text-[#F39C12] font-semibold text-sm">{product.purchase_price_ht ? `${product.purchase_price_ht.toLocaleString('fr-FR')} €` : '-'}</p>
          </div>
          {product.margin_percent && (
            <div className="col-span-3 text-right">
              <span className="text-[10px] text-[#8C8F94]">Marge: </span>
              <span className="text-[10px] text-white font-medium">{product.margin_percent}%</span>
            </div>
          )}
        </div>
      ) : canSeePrices ? (
        <div className="flex items-center justify-between pt-3 border-t border-[#2C3338]">
          <div><p className="text-[#8C8F94] text-xs">Achat HT</p><p className="text-white font-semibold">{product.purchase_price_ht ? `${product.purchase_price_ht.toLocaleString('fr-FR')} €` : '-'}</p></div>
          <div className="text-right"><p className="text-[#8C8F94] text-xs">Vente TTC</p><p className="text-[#00E01A] font-semibold">{product.sale_price_ttc ? `${product.sale_price_ttc.toLocaleString('fr-FR')} €` : '-'}</p></div>
          {product.margin_percent && (<div className="text-right"><p className="text-[#8C8F94] text-xs">Marge</p><p className="text-white font-semibold">{product.margin_percent}%</p></div>)}
        </div>
      ) : null}
      <Link to={`/products/${product.id}`} className="flex items-center justify-between text-sm text-[#8C8F94] group-hover:text-[#00E01A] transition-colors mt-3 pt-3 border-t border-[#2C3338]" data-testid={`product-link-${product.id}`}>
        <span>Voir la fiche</span><ChevronRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  </div>
);

const SupplierCard = ({ supplier, productCount, categories, onClick, onHeroUpload }) => {
  const bgImage = supplier.hero_image ? `${API_URL}${supplier.hero_image}` : brandImages[supplier.name];

  return (
    <div
      className="relative group w-full text-left rounded-2xl overflow-hidden border border-[#2C3338] hover:border-[#00E01A]/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,224,26,0.1)]"
      data-testid={`supplier-card-${supplier.id}`}
    >
      {/* Hero image upload button */}
      {onHeroUpload && (
        <label className="absolute top-3 right-3 z-20 flex items-center gap-1.5 bg-black/60 backdrop-blur hover:bg-[#00E01A] hover:text-black px-3 py-1.5 rounded-lg text-xs text-white cursor-pointer transition-all opacity-0 group-hover:opacity-100" data-testid={`upload-hero-${supplier.id}`}>
          <Upload size={12} />
          Changer la photo
          <input type="file" accept="image/*" className="hidden" onChange={e => { e.stopPropagation(); e.target.files?.[0] && onHeroUpload(supplier.id, e.target.files[0]); }} />
        </label>
      )}
      {/* Clickable area */}
      <button onClick={onClick} className="w-full text-left">
      {/* Background image */}
      <div className="aspect-[16/9] relative">
        {bgImage ? (
          <img src={bgImage} alt={supplier.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1D2327] to-[#141617] flex items-center justify-center">
            <Package size={64} className="text-[#2C3338]" />
          </div>
        )}
        {/* Dark overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

        {/* Content over image */}
        <div className="absolute inset-0 flex flex-col justify-end p-6">
          {/* Brand name */}
          <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight font-['Outfit'] drop-shadow-lg">
            {supplier.name}
          </h2>

          {/* Info row */}
          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1.5 text-[#ccc] text-sm">
              <MapPin size={14} />
              {supplier.country}
            </span>
            {supplier.relation_type && (
              <span className="flex items-center gap-1.5 text-[#ccc] text-sm">
                <ShieldCheck size={14} />
                {relationLabels[supplier.relation_type] || supplier.relation_type}
              </span>
            )}
          </div>

          {/* Categories + count */}
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <span key={cat} className={`px-2 py-0.5 rounded-full text-xs font-medium border ${categoryColors[cat]}`}>
                  {categoryLabels[cat]}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full">
              <span className="text-[#00E01A] font-bold text-lg">{productCount}</span>
              <span className="text-[#aaa] text-xs">produits</span>
            </div>
          </div>
        </div>

        {/* Hover arrow */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-[#00E01A] rounded-full p-2">
            <ChevronRight size={20} className="text-black" />
          </div>
        </div>
      </div>
      </button>
    </div>
  );
};

const Products = () => {
  const { api, isSuperAdmin, canSeePrices } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // Selected supplier from URL or state
  const selectedSupplierId = searchParams.get('supplier') || null;

  const setSelectedSupplier = (supplierId) => {
    if (supplierId) {
      setSearchParams({ supplier: supplierId });
    } else {
      setSearchParams({});
    }
    setSearch('');
    setCategoryFilter('all');
  };

  const fetchData = async () => {
    try {
      const [productsRes, suppliersRes] = await Promise.all([
        api.get('/products'),
        api.get('/suppliers')
      ]);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [api]);

  const handleSave = async (data) => {
    try {
      if (editingProduct) {
        toast.info('Mise à jour non disponible');
      } else {
        await api.post('/products', data);
        toast.success('Produit créé');
      }
      fetchData();
      setDialogOpen(false);
      setEditingProduct(null);
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleExcelImport = async (formData, supplierId) => {
    const response = await api.post(`/products/import-excel?supplier_id=${supplierId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    fetchData();
    return response.data;
  };

  const handleProductPhoto = async (productId, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/products/${productId}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo ajoutee');
      fetchData();
    } catch { toast.error('Erreur upload photo'); }
  };

  const handleHeroUpload = async (supplierId, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/suppliers/${supplierId}/hero-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success("Photo d'accueil mise a jour");
      fetchData();
    } catch { toast.error('Erreur upload'); }
  };

  // Compute supplier stats from products
  const supplierStats = suppliers.map((s) => {
    const supplierProducts = products.filter((p) => p.supplier_id === s.id);
    const categories = [...new Set(supplierProducts.map((p) => p.category))];
    return { supplier: s, productCount: supplierProducts.length, categories };
  }).filter(s => s.productCount > 0);

  // Global search results (across all suppliers)
  const globalSearchResults = search.trim() && !selectedSupplierId
    ? products.filter((p) => {
        const q = search.toLowerCase();
        return p.name.toLowerCase().includes(q) || p.reference.toLowerCase().includes(q) || (p.specs?.product_type || '').toLowerCase().includes(q);
      })
    : [];

  // Filtered products for product grid view
  const filteredProducts = products.filter((p) => {
    if (!selectedSupplierId) return false;
    const matchesSupplier = p.supplier_id === selectedSupplierId;
    const matchesSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.reference.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || getSubCategory(p) === categoryFilter;
    return matchesSupplier && matchesSearch && matchesCategory;
  });

  // Available sub-categories for the current supplier (for filter dropdown)
  const availableSubCategories = selectedSupplierId
    ? subCategoryOrder.filter((sc) =>
        products.some((p) => p.supplier_id === selectedSupplierId && getSubCategory(p) === sc)
      )
    : [];

  const selectedSupplier = suppliers.find((s) => s.id === selectedSupplierId);
  const totalSupplierProducts = selectedSupplierId ? products.filter((p) => p.supplier_id === selectedSupplierId).length : 0;

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-2xl overflow-hidden animate-pulse">
              <div className="aspect-[16/9] bg-[#1D2327]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ====== SUPPLIER LANDING PAGE ======
  if (!selectedSupplierId) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Catalogue produits</h1>
            <p className="text-[#8C8F94] mt-1">{products.length} produit{products.length > 1 ? 's' : ''} au catalogue</p>
          </div>
          {isSuperAdmin() && (
            <div className="flex items-center gap-2">
              <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                <DialogTrigger asChild>
                  <button className="flex items-center gap-2 bg-[#1D2327] hover:bg-[#2C3338] text-white font-medium px-4 py-2.5 rounded-full border border-[#2C3338] transition-all" data-testid="import-excel-btn">
                    <FileSpreadsheet size={18} /><span className="hidden md:inline">Import Excel</span>
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-md">
                  <DialogHeader><DialogTitle className="text-xl font-bold font-['Outfit']">Importer depuis Excel</DialogTitle></DialogHeader>
                  <ExcelImportDialog suppliers={suppliers} onImport={handleExcelImport} onClose={() => setImportDialogOpen(false)} />
                </DialogContent>
              </Dialog>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <button onClick={() => setEditingProduct(null)} className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)] hover:shadow-[0_0_25px_rgba(0,224,26,0.4)]" data-testid="add-product-btn">
                    <Plus size={18} /><span className="hidden md:inline">Ajouter</span>
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-2xl">
                  <DialogHeader><DialogTitle className="text-xl font-bold font-['Outfit']">{editingProduct ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle></DialogHeader>
                  <ProductForm product={editingProduct} suppliers={suppliers} onSave={handleSave} onClose={() => { setDialogOpen(false); setEditingProduct(null); }} />
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Global search bar */}
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8F94]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un produit par nom, référence..."
            className="w-full bg-[#1D2327] border border-[#2C3338] rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-[#52555A] focus:outline-none focus:border-[#00E01A] transition-colors"
            data-testid="global-product-search"
          />
        </div>

        {/* Show search results or brand grid */}
        {search.trim() ? (
          <div className="space-y-4">
            <p className="text-[#8C8F94] text-sm">
              {globalSearchResults.length} résultat{globalSearchResults.length > 1 ? 's' : ''} pour "{search}"
            </p>
            {globalSearchResults.length === 0 ? (
              <div className="text-center py-12">
                <Search size={48} className="mx-auto text-[#2C3338] mb-4" />
                <p className="text-[#8C8F94]">Aucun produit trouvé</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {globalSearchResults.slice(0, 40).map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    canSeePrices={canSeePrices()}
                    onEdit={(p) => { setEditingProduct(p); setDialogOpen(true); }}
                    onPhotoUpload={isSuperAdmin() ? handleProductPhoto : null}
                  />
                ))}
              </div>
            )}
            {globalSearchResults.length > 40 && (
              <p className="text-[#52555A] text-sm text-center">... et {globalSearchResults.length - 40} autres résultats. Précisez votre recherche.</p>
            )}
          </div>
        ) : (
        <>
        {/* Brand Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {supplierStats.map(({ supplier, productCount, categories }) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              productCount={productCount}
              categories={categories}
              onClick={() => setSelectedSupplier(supplier.id)}
              onHeroUpload={isSuperAdmin() ? handleHeroUpload : null}
            />
          ))}
        </div>

        {supplierStats.length === 0 && (
          <div className="text-center py-16">
            <Package size={56} className="mx-auto text-[#2C3338] mb-4" />
            <p className="text-[#8C8F94] text-lg">Aucun fournisseur avec des produits</p>
            <p className="text-[#52555A] text-sm mt-1">Ajoutez des produits via le bouton ci-dessus</p>
          </div>
        )}
        </>
        )}
      </div>
    );
  }

  // ====== PRODUCT LIST VIEW (SUPPLIER SELECTED) ======
  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header with back button */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelectedSupplier(null)}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-[#1D2327] border border-[#2C3338] hover:border-[#00E01A]/50 text-[#8C8F94] hover:text-white transition-all"
            data-testid="back-to-brands-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
              {selectedSupplier?.name || 'Catalogue'}
            </h1>
            <p className="text-[#8C8F94] mt-1">
              {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''}
              {filteredProducts.length !== totalSupplierProducts && ` (sur ${totalSupplierProducts})`}
            </p>
          </div>
        </div>
        {isSuperAdmin() && (
          <div className="flex items-center gap-2">
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <button className="flex items-center gap-2 bg-[#1D2327] hover:bg-[#2C3338] text-white font-medium px-4 py-2.5 rounded-full border border-[#2C3338] transition-all" data-testid="import-excel-btn">
                  <FileSpreadsheet size={18} /><span className="hidden md:inline">Import Excel</span>
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-md">
                <DialogHeader><DialogTitle className="text-xl font-bold font-['Outfit']">Importer depuis Excel</DialogTitle></DialogHeader>
                <ExcelImportDialog suppliers={suppliers} onImport={handleExcelImport} onClose={() => setImportDialogOpen(false)} />
              </DialogContent>
            </Dialog>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <button onClick={() => setEditingProduct(null)} className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(0,224,26,0.3)] hover:shadow-[0_0_25px_rgba(0,224,26,0.4)]" data-testid="add-product-btn">
                  <Plus size={18} /><span className="hidden md:inline">Ajouter</span>
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-2xl">
                <DialogHeader><DialogTitle className="text-xl font-bold font-['Outfit']">{editingProduct ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle></DialogHeader>
                <ProductForm product={editingProduct} suppliers={suppliers} onSave={handleSave} onClose={() => { setDialogOpen(false); setEditingProduct(null); }} />
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Category icon bar */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-testid="category-icon-bar">
        <button
          onClick={() => setCategoryFilter('all')}
          className={`flex flex-col items-center gap-1.5 min-w-[80px] px-3 py-3 rounded-xl border transition-all duration-200 shrink-0 ${
            categoryFilter === 'all'
              ? 'bg-[#00E01A]/10 border-[#00E01A]/40 text-[#00E01A] shadow-[0_0_12px_rgba(0,224,26,0.15)]'
              : 'bg-[#1D2327] border-[#2C3338] text-[#8C8F94] hover:border-[#52555A] hover:text-white'
          }`}
          data-testid="category-icon-all"
        >
          <LayoutGrid size={22} />
          <span className="text-[11px] font-medium leading-tight text-center">Tout</span>
          <span className={`text-[10px] font-bold ${categoryFilter === 'all' ? 'text-[#00E01A]' : 'text-[#52555A]'}`}>{totalSupplierProducts}</span>
        </button>
        {availableSubCategories.map((sc) => {
          const Icon = subCategoryIcons[sc] || Package;
          const count = products.filter((p) => p.supplier_id === selectedSupplierId && getSubCategory(p) === sc).length;
          const isActive = categoryFilter === sc;
          const colorBase = subCategoryColors[sc]?.match(/text-\[(#[A-Fa-f0-9]+)\]/)?.[1] || '#8C8F94';
          return (
            <button
              key={sc}
              onClick={() => setCategoryFilter(sc === categoryFilter ? 'all' : sc)}
              className={`flex flex-col items-center gap-1.5 min-w-[80px] px-3 py-3 rounded-xl border transition-all duration-200 shrink-0 ${
                isActive
                  ? `border-[${colorBase}]/40 shadow-[0_0_12px_${colorBase}26]`
                  : 'bg-[#1D2327] border-[#2C3338] text-[#8C8F94] hover:border-[#52555A] hover:text-white'
              }`}
              style={isActive ? { backgroundColor: `${colorBase}15`, borderColor: `${colorBase}66`, color: colorBase } : {}}
              data-testid={`category-icon-${sc}`}
            >
              <Icon size={22} />
              <span className="text-[11px] font-medium leading-tight text-center whitespace-nowrap">{subCategoryLabels[sc]}</span>
              <span className="text-[10px] font-bold" style={isActive ? { color: colorBase } : { color: '#52555A' }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8F94]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un produit..."
          className="w-full bg-[#1D2327] border border-[#2C3338] rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-[#52555A] focus:outline-none focus:border-[#00E01A] transition-colors"
          data-testid="product-search-input"
        />
      </div>

      {/* Products Grid - Grouped by sub-category */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12">
          <Package size={48} className="mx-auto text-[#8C8F94] mb-4" />
          <p className="text-[#8C8F94]">{search || categoryFilter !== 'all' ? 'Aucun produit trouvé' : 'Aucun produit pour ce fournisseur'}</p>
        </div>
      ) : (
        <div className="space-y-8">
          {subCategoryOrder
            .map((sc) => {
              const scProducts = filteredProducts.filter((p) => getSubCategory(p) === sc);
              if (scProducts.length === 0) return null;
              return (
                <div key={sc} data-testid={`subcategory-section-${sc}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${subCategoryColors[sc]}`}>
                      {subCategoryLabels[sc]}
                    </span>
                    <span className="text-[#52555A] text-sm">{scProducts.length} produit{scProducts.length > 1 ? 's' : ''}</span>
                    <div className="flex-1 h-px bg-[#2C3338]" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {scProducts.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        canSeePrices={canSeePrices()}
                        onEdit={(p) => { setEditingProduct(p); setDialogOpen(true); }}
                        onPhotoUpload={isSuperAdmin() ? handleProductPhoto : null}
                      />
                    ))}
                  </div>
                </div>
              );
            })
            .filter(Boolean)}
        </div>
      )}
    </div>
  );
};

export default Products;
